// ============================================================
// Toplu duyuru ve veli iletişimi (§13)
//
// `messages` birebir yazışma için kurulmuştu; sınıfa veya tüm kuruma
// duyuru gönderme yolu yoktu. Bu iş bugün WhatsApp gruplarından
// yürüyor — kurum içine çekmek ürünün değerini artırır ve KVKK
// açısından da doğrusu budur (veli telefonları grupta herkese açık
// olmaktan çıkar).
//
// Üç yetenek:
//   1. Hedefli duyuru — kurum / sınıf / servis kullananlar / borçlular
//   2. Onay isteyen duyuru — gezi izin formu gibi
//   3. Anket — memnuniyet, menü tercihi
// ============================================================

import { BUGUN } from "../config.js";

import { B, subeBulHerYerde } from "./context.js";
import { faturaDurumu } from "./invoices.js";
import { veliler } from "./students.js";


const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

/**
 * Hedef kitleler. Her biri şubedeki öğrenci listesini süzer;
 * alıcı listesi velilerdir.
 */
export const HEDEFLER = {
  kurum:   { label: "Tüm kurum",        secer: t => t.students },
  sinif:   { label: "Belirli sınıf",    secer: (t, p) => t.students.filter(s => s.classId === p) },
  servis:  { label: "Servis kullananlar", secer: t => t.students.filter(s => s.service) },
  borclu:  { label: "Açık bakiyesi olanlar",
             secer: t => t.students.filter(s =>
               t.payments.some(p => p.studentId === s.id &&
                 ["gecikti", "bekliyor", "kismi"].includes(faturaDurumu(s, p)))) },
  yarimGun: { label: "Yarım gün öğrenciler", secer: t => t.students.filter(s => s.schedule === "yarim") },
};

export const TURLER = {
  duyuru: { label: "Duyuru", icon: "📢", onayIster: false, anket: false },
  onay:   { label: "Onay isteyen", icon: "✍️", onayIster: true, anket: false },
  anket:  { label: "Anket", icon: "📊", onayIster: false, anket: true },
};

// ---------- Okuma ----------

export const duyurular = branchId =>
  [...(sube(branchId).announcements ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

/** Duyurunun alıcı öğrencileri (dolayısıyla velileri). */
export function aliciOgrenciler(duyuru, branchId) {
  const t = sube(branchId);
  const hedef = HEDEFLER[duyuru.hedef];
  if (!hedef) return [];
  return hedef.secer(t, duyuru.hedefParam);
}

/** Duyurunun ulaştığı veli sayısı — aynı veli iki çocuk için tek sayılır. */
export function aliciVeliSayisi(duyuru, branchId) {
  const adlar = new Set(
    aliciOgrenciler(duyuru, branchId).map(s => veliler(s, branchId)[0]?.name));
  return adlar.size;
}

/**
 * Duyuru durumu: kaç kişiye ulaştı, kaçı okudu, kaçı yanıtladı.
 * Onay isteyen duyuruda "yanıt" = onay/ret; ankette = seçenek.
 */
export function duyuruDurumu(duyuru, branchId) {
  const alicilar = aliciOgrenciler(duyuru, branchId);
  const yanitlar = duyuru.responses ?? {};
  const okuyanlar = Object.values(yanitlar).filter(y => y.readAt).length;
  const yanitlayanlar = Object.values(yanitlar).filter(y => y.answer != null);

  const dagilim = {};
  for (const y of yanitlayanlar) dagilim[y.answer] = (dagilim[y.answer] ?? 0) + 1;

  const sonGun = duyuru.deadline ?? null;

  return {
    alici: alicilar.length,
    okuyan: okuyanlar,
    yanitlayan: yanitlayanlar.length,
    bekleyen: alicilar.length - yanitlayanlar.length,
    dagilim,
    okumaOrani: alicilar.length ? Math.round((okuyanlar / alicilar.length) * 100) : 0,
    yanitOrani: alicilar.length ? Math.round((yanitlayanlar.length / alicilar.length) * 100) : 0,
    sonGun,
    suresiGecti: Boolean(sonGun && sonGun < BUGUN),
    yanitBekleyenler: alicilar.filter(s => yanitlar[s.id]?.answer == null),
  };
}

/** Velinin göreceği duyurular — kendi çocuğunu kapsayanlar. */
export function veliDuyurulari(student, branchId) {
  return duyurular(branchId)
    .filter(d => aliciOgrenciler(d, branchId).some(s => s.id === student.id))
    .map(d => ({
      ...d,
      yanit: d.responses?.[student.id] ?? null,
      yanitGerekli: TURLER[d.tur]?.onayIster || TURLER[d.tur]?.anket,
    }));
}

/** Yanıt bekleyen duyurusu olan öğrenciler — yöneticiye takip listesi. */
export function yanitBekleyenler(branchId) {
  return duyurular(branchId)
    .filter(d => TURLER[d.tur]?.onayIster || TURLER[d.tur]?.anket)
    .filter(d => !duyuruDurumu(d, branchId).suresiGecti)
    .flatMap(d => duyuruDurumu(d, branchId).yanitBekleyenler.map(s => ({ duyuru: d, student: s })));
}

export function duyuruOzeti(branchId) {
  const list = duyurular(branchId);
  const acikOnay = list.filter(d =>
    (TURLER[d.tur]?.onayIster || TURLER[d.tur]?.anket) && !duyuruDurumu(d, branchId).suresiGecti);

  return {
    toplam: list.length,
    acikOnay: acikOnay.length,
    yanitBekleyen: yanitBekleyenler(branchId).length,
    sonDuyuru: list[0] ?? null,
  };
}
